import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check, X, Star, HelpCircle } from "lucide-react";
import SEO from "../components/SEO";
import Reveal from "../components/Reveal";
import api from "../lib/api";
import { useSite } from "../context/SiteContext";
import { Testimonials } from "./Launch";

const COURSE_SLUG = "the-independent-artist-blueprint";
const FOR = ["Independent artists releasing their own music", "Musicians tired of guessing what to do next", "Creators who want a real strategy, not hacks"];
const NOTFOR = ["Anyone looking for overnight fame", "Artists unwilling to do the work", "People wanting a magic algorithm trick"];
const FAQ = [
    ["Do I need experience?", "No. The Blueprint starts from foundations and builds up to advanced release strategy."],
    ["How long do I have access?", "Lifetime access, including future updates to the course."],
    ["Is there a certificate?", "Yes, complete all lessons to unlock your MUVIARTS certificate."],
    ["Can MUVIARTS also produce my music?", "Absolutely. Many students go on to work with us on production, visuals and marketing."],
];

export default function Blueprint() {
    const { openLead } = useSite();
    const navigate = useNavigate();
    const [course, setCourse] = useState(null);
    const [tests, setTests] = useState([]);
    const [faq, setFaq] = useState(-1);

    useEffect(() => {
        api.get(`/courses/${COURSE_SLUG}`).then((r) => setCourse(r.data)).catch(() => {});
        api.get("/testimonials").then((r) => setTests(r.data)).catch(() => {});
    }, []);

    return (
        <div data-testid="blueprint-page">
            <SEO title="Independent Artist Blueprint, Course" description="A complete roadmap for independent artists to build, release and grow their music career without a label." />
            <section className="relative bg-espresso text-white overflow-hidden">
                <img loading="lazy" decoding="async" src="https://images.unsplash.com/photo-1584582845121-42b1a7641ad7?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000" alt="Blueprint" className="absolute inset-0 h-full w-full object-cover opacity-35" />
                <div className="absolute inset-0 bg-espresso/55" />
                <div className="relative mx-container py-20 lg:py-24 text-center">
                    <p className="overline !text-brand-gold mb-4">MUVIARTS Academy · Flagship Course</p>
                    <h1 className="font-display font-semibold text-5xl sm:text-7xl leading-[0.98] max-w-3xl mx-auto">The Independent <span className="italic text-brand-gold">Artist Blueprint.</span></h1>
                    <p className="mt-6 max-w-xl mx-auto text-white/70 text-lg">A complete roadmap to build, release and grow your music career, without depending entirely on a traditional label.</p>
                    <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                        <Link to={`/courses/${COURSE_SLUG}`} className="btn-primary" data-testid="blueprint-start">Start Learning <ArrowRight className="h-4 w-4" /></Link>
                        <button onClick={() => openLead?.("Course / Academy")} className="btn-ghost-light !px-2 uppercase text-sm font-semibold tracking-wide" data-testid="blueprint-ask">Ask a question <ArrowRight className="h-4 w-4" /></button>
                    </div>
                </div>
            </section>

            {course && (
                <section className="py-16 lg:py-24 bg-white">
                    <div className="mx-container grid lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-8">
                            <p className="overline mb-3">What you'll learn</p>
                            <ul className="grid sm:grid-cols-2 gap-3 mb-12">
                                {(course.outcomes || []).map((o) => (<li key={o} className="flex items-start gap-2.5 text-neutral-700"><Check className="h-5 w-5 text-brand-gold shrink-0" /> {o}</li>))}
                            </ul>
                            <p className="overline mb-3">Curriculum</p>
                            <div className="border border-[#e8e1d4] divide-y divide-[#f0ead9]">
                                {(course.modules || []).map((m, i) => (
                                    <div key={i} className="flex items-center justify-between px-5 py-4"><div className="flex items-center gap-4"><span className="big-index text-xl text-[#d8ccb4]">{String(i + 1).padStart(2, "0")}</span><span className="font-semibold">{m.title}</span></div><span className="text-sm text-neutral-400">{m.lessons} lessons</span></div>
                                ))}
                            </div>
                        </div>
                        <div className="lg:col-span-4">
                            <div className="sticky top-24 border border-[#e8e1d4] p-6 bg-cream">
                                {!course.coming_soon && <p className="font-display font-semibold text-3xl">₹{course.price?.toLocaleString("en-IN")}</p>}
                                {!course.coming_soon && Number(course.original_price) > Number(course.price) && <p className="text-neutral-400 line-through">₹{Number(course.original_price).toLocaleString("en-IN")}</p>}
                                <Link to={`/courses/${COURSE_SLUG}`} className="btn-primary w-full mt-5" data-testid="blueprint-buy">{course.coming_soon ? "Notify Me" : "Enroll Now"}</Link>
                                {!course.coming_soon && Number(course.reviews) > 0 && <div className="mt-4 flex items-center gap-1.5 text-sm text-neutral-600"><Star className="h-4 w-4 fill-brand-gold text-brand-gold" />{course.rating} · {course.students?.toLocaleString("en-IN")} students</div>}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            <section className="py-16 bg-cream border-y border-[#e8e1d4]">
                <div className="mx-container grid md:grid-cols-2 gap-10">
                    <div><h3 className="font-display font-semibold text-2xl mb-4">Who it's for</h3><ul className="space-y-2.5">{FOR.map((f) => (<li key={f} className="flex items-start gap-2.5 text-neutral-700"><Check className="h-5 w-5 text-brand-gold shrink-0" /> {f}</li>))}</ul></div>
                    <div><h3 className="font-display font-semibold text-2xl mb-4">Who it's not for</h3><ul className="space-y-2.5">{NOTFOR.map((f) => (<li key={f} className="flex items-start gap-2.5 text-neutral-500"><X className="h-5 w-5 text-neutral-400 shrink-0" /> {f}</li>))}</ul></div>
                </div>
            </section>

            {tests.length > 0 && <Testimonials tests={tests} />}

            <section className="py-16 lg:py-24 bg-white">
                <div className="mx-container max-w-3xl">
                    <p className="overline mb-3">FAQ</p>
                    <div className="divide-y divide-[#f0ead9] border-t border-[#e8e1d4]">
                        {FAQ.map(([q, a], i) => (
                            <div key={i} className="py-4">
                                <button onClick={() => setFaq(faq === i ? -1 : i)} className="w-full flex items-center justify-between text-left" data-testid={`faq-${i}`}>
                                    <span className="font-display font-semibold text-lg">{q}</span>
                                    <HelpCircle className={`h-5 w-5 shrink-0 transition-colors ${faq === i ? "text-brand-gold" : "text-neutral-300"}`} />
                                </button>
                                {faq === i && <p className="mt-3 text-neutral-500 leading-relaxed">{a}</p>}
                            </div>
                        ))}
                    </div>
                    <div className="mt-10 text-center">
                        <Link to={`/courses/${COURSE_SLUG}`} className="btn-primary" data-testid="blueprint-cta-bottom">Start Learning <ArrowRight className="h-4 w-4" /></Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
